import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../services/api'
import PrivatePageSection from '../../components/layout/PrivatePageSection'
import { normalizeArray, readByKeys, toSafeText } from '../../utils/defensiveData'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'

const normalizeProductLabels = (user, productsCatalog) => {
  const directProducts = normalizeArray(
    readByKeys(user, ['products', 'assignedProducts', 'productList'], [])
  )

  if (directProducts.length > 0) {
    return directProducts
      .map((entry) => {
        if (typeof entry === 'object' && entry !== null) {
          return toSafeText(
            entry.name_product ||
              entry.nameProduct ||
              entry.name ||
              entry.label ||
              entry.id,
            ''
          )
        }
        return toSafeText(entry, '')
      })
      .filter(Boolean)
  }

  const userProductId = readByKeys(user, ['product_id', 'productId'], null)
  if (userProductId !== null && userProductId !== undefined && productsCatalog.length > 0) {
    const fromCatalog = productsCatalog.find(
      (product) => String(readByKeys(product, ['id'], '')) === String(userProductId)
    )

    if (fromCatalog) {
      return [
        toSafeText(
          readByKeys(fromCatalog, ['name_product', 'nameProduct', 'name'], ''),
          ''
        ),
      ].filter(Boolean)
    }

    return [toSafeText(userProductId, '')].filter(Boolean)
  }

  return []
}

const UserDetail = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userId } = useParams()

  const seededUser = location?.state?.user || null

  const [user, setUser] = useState(seededUser)
  const [clientName, setClientName] = useState('')
  const [productLabels, setProductLabels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true)
      setError('')

      try {
        const userResponse = await api.get(`/users/${userId}`)
        const userPayload = userResponse?.data || null
        setUser(userPayload)

        const resolvedClientId = readByKeys(userPayload, ['client_id', 'clientId'], null)

        const [clientResult, productsResult] = await Promise.allSettled([
          resolvedClientId ? api.get(`/clients/${resolvedClientId}`) : Promise.resolve(null),
          api.get('/products'),
        ])

        if (clientResult.status === 'fulfilled') {
          const clientPayload = clientResult.value?.data || null
          const normalizedClientName = toSafeText(
            readByKeys(clientPayload, ['full_name_or_company', 'fullNameOrCompany', 'name'], ''),
            ''
          )
          setClientName(normalizedClientName)
        } else {
          setClientName('')
        }

        if (productsResult.status === 'fulfilled') {
          const productsPayload = normalizeArray(productsResult.value?.data)
          setProductLabels(normalizeProductLabels(userPayload, productsPayload))
        } else {
          setProductLabels([])
        }
      } catch (requestError) {
        setUser(null)
        setClientName('')
        setProductLabels([])
        setError('No fue posible cargar el detalle del usuario.')
      } finally {
        setLoading(false)
      }
    }

    if (!userId) {
      setError('No se encontró un identificador de usuario válido.')
      setLoading(false)
      return
    }

    fetchDetail()
  }, [userId])

  const fullName = useMemo(() => {
    const names = toSafeText(readByKeys(user, ['names'], ''), '')
    const lastNameF = toSafeText(readByKeys(user, ['lastname_f', 'lastNameP'], ''), '')
    const lastNameM = toSafeText(readByKeys(user, ['lastname_m', 'lastNameM'], ''), '')
    const assembled = `${names} ${lastNameF} ${lastNameM}`.trim()
    return assembled || 'Sin nombre'
  }, [user])

  const email = useMemo(
    () => toSafeText(readByKeys(user, ['email'], '-'), '-'),
    [user]
  )

  const resolvedClientId = useMemo(
    () => toSafeText(readByKeys(user, ['client_id', 'clientId'], 'N/A'), 'N/A'),
    [user]
  )

  const roleLabel = useMemo(() => {
    const roles = normalizeArray(readByKeys(user, ['roles', 'roleTypes'], []))

    if (roles.length > 0) {
      return roles
        .map((role) => {
          if (typeof role === 'object' && role !== null) {
            return toSafeText(role.type || role.name_role || role.name || role.label, '')
          }
          return toSafeText(role, '')
        })
        .filter(Boolean)
        .join(', ') || 'No especificado'
    }

    return toSafeText(readByKeys(user, ['role', 'roleType'], 'No especificado'), 'No especificado')
  }, [user])

  const scopeLabel = useMemo(() => {
    const value = readByKeys(user, ['scope', 'scopeInfo', 'operationalScope'], null)
    return toSafeText(value, 'No especificado')
  }, [user])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !user) {
    return (
      <PrivatePageSection title="Detalle de Usuario">
        <Stack spacing={2}>
          <Alert severity="error">{error || 'Usuario no encontrado.'}</Alert>
          <Box>
            <Button variant="outlined" onClick={() => navigate('/users')}>
              Volver a usuarios
            </Button>
          </Box>
        </Stack>
      </PrivatePageSection>
    )
  }

  return (
    <PrivatePageSection
      title={fullName}
      subtitle="Ficha operativa de usuario"
      actions={
        <Button variant="outlined" onClick={() => navigate('/users')}>
          Volver a usuarios
        </Button>
      }
    >
      <Stack spacing={2.5}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {fullName}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Chip size="small" label={`ID: ${toSafeText(readByKeys(user, ['id'], '—'), '—')}`} />
                <Chip size="small" variant="outlined" label={`Cliente: ${resolvedClientId}`} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Datos generales
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2">Email: {email}</Typography>
                <Typography variant="body2">Cliente asociado: {clientName || resolvedClientId}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Rol y alcance
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2">Rol: {roleLabel}</Typography>
                <Typography variant="body2">Alcance: {scopeLabel}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Productos asociados
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {productLabels.length > 0 ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {productLabels.map((label) => (
                  <Chip key={label} variant="outlined" label={toSafeText(label, 'Producto')} />
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No hay productos asociados para este usuario.</Alert>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Indicadores simples
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2">Total de productos asociados: {productLabels.length}</Typography>
          </CardContent>
        </Card>
      </Stack>
    </PrivatePageSection>
  )
}

export default UserDetail
